import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, FileDown, X } from "lucide-react";
import { api, AYLAR } from "@/lib/api";
import { saveBlob } from "./bankUtils";

export default function PayrollImportModal({ yil, ay, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  useEffect(() => {
    const k = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onClose]);

  const downloadTemplate = () => {
    let hp = "";
    try {
      const raw = localStorage.getItem("payroll.hiddenCols.v2");
      if (raw) {
        const arr = JSON.parse(raw) || [];
        if (arr.length) hp = `&hidden_cols=${encodeURIComponent(arr.join(","))}`;
      }
    } catch {}
    api.get(`/payroll/import/template?yil=${yil}&ay=${ay}${hp}`, { responseType: "blob" })
      .then((r) => saveBlob(r, `bordro_sablon_${yil}_${ay}.xlsx`)).catch(() => toast.error("Şablon indirilemedi"));
  };

  const handleUpload = async () => {
    if (!file) { toast.error("Önce dosya seçin"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post(`/payroll/import?yil=${yil}&ay=${ay}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(res.data);
      if (res.data.updated > 0) {
        toast.success(`${res.data.updated} satır güncellendi`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "İçe aktarılamadı");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4">
      <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-xl" data-testid="payroll-import-modal">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold flex items-center gap-2">
            <Upload size={18} /> Toplu Bordro İçe Aktar · {AYLAR[ay-1].n} {yil}
          </h3>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-100 grid place-items-center"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900">
            <p className="font-semibold mb-1">Nasıl Çalışır?</p>
            <ol className="list-decimal list-inside space-y-0.5 text-xs">
              <li>Şablon mevcut personel + bu aya ait kayıtlı değerlerle dolu gelir</li>
              <li>Çalışma günü, izin, mesai, yemek vb. değerleri düzenle</li>
              <li>Dosyayı yükle - TC kimlik numarası ile eşleşen personelin bordrosu güncellenir</li>
            </ol>
          </div>
          <button
            data-testid="download-payroll-template"
            onClick={downloadTemplate}
            className="w-full flex items-center justify-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-900 px-4 py-3 rounded-lg font-semibold text-sm transition-colors"
          >
            <FileDown size={16} /> Şablonu İndir (bu ayın verileriyle dolu)
          </button>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">
              Yüklenecek Dosya
            </label>
            <input
              data-testid="payroll-import-file"
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="w-full text-sm text-slate-700 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-slate-900 file:text-white file:font-semibold hover:file:bg-slate-800 cursor-pointer"
            />
            {file && (
              <div className="mt-2 text-xs text-slate-500">Seçili: {file.name} ({(file.size / 1024).toFixed(1)} KB)</div>
            )}
          </div>
          {result && (
            <div className={`rounded-lg p-3 text-sm border ${result.updated > 0 ? "bg-emerald-50 border-emerald-200" : "bg-rose-50 border-rose-200"}`}>
              <div className="font-semibold mb-1">
                {result.updated > 0 ? `✓ ${result.updated} satır başarıyla güncellendi` : "Hiç satır güncellenemedi"}
              </div>
              {result.errors && result.errors.length > 0 && (
                <div className="mt-2 text-xs">
                  <div className="font-semibold text-amber-700 mb-1">Uyarılar ({result.errors.length}):</div>
                  <ul className="list-disc list-inside max-h-32 overflow-y-auto space-y-0.5 text-slate-600">
                    {result.errors.map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={result?.updated > 0 ? onDone : onClose} className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-semibold">
              {result?.updated > 0 ? "Kapat & Yenile" : "İptal"}
            </button>
            <button
              data-testid="upload-payroll-import"
              onClick={handleUpload}
              disabled={!file || uploading}
              className="px-4 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold disabled:opacity-50"
            >
              {uploading ? "Yükleniyor..." : "İçe Aktar"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
